import Experience from '../Experience.js'
import { PRESETS } from '../../presets/registry.js'

/**
 * Manages the 5 preset WebGL cards on the landing page.
 * Delegates all WebGL logic to preset instances from the registry.
 * Passes simulated audio values when no audio is playing to preserve pulsing animations.
 */
export default class PresetCards {
    constructor() {
        this.experience = new Experience()
        this.time       = this.experience.time
        this.sizes      = this.experience.sizes

        this.cards = []
        this._init()

        this.sizes.on('resize', () => this._onResize())
    }

    _init() {
        const cardEls = document.querySelectorAll('.preset-card')
        cardEls.forEach((el, i) => {
            if (i >= PRESETS.length) return

            const dpr    = Math.min(window.devicePixelRatio, 2)
            const canvas = document.createElement('canvas')
            canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;'

            const bgEl = el.querySelector('.bg')
            bgEl ? bgEl.replaceWith(canvas) : el.insertBefore(canvas, el.firstChild)

            canvas.width  = el.offsetWidth  * dpr
            canvas.height = el.offsetHeight * dpr

            const preset = PRESETS[i]
            preset.init(canvas)

            const card = { cardEl: el, canvas, dpr, preset, hoverTarget: 0, hoverCurrent: 0 }
            el.addEventListener('mouseenter', () => { card.hoverTarget = 1 })
            el.addEventListener('mouseleave', () => { card.hoverTarget = 0 })
            this.cards.push(card)
        })
    }

    _onResize() {
        this.cards.forEach(({ canvas, cardEl, dpr }) => {
            canvas.width  = cardEl.offsetWidth  * dpr
            canvas.height = cardEl.offsetHeight * dpr
            // BasePreset.tick() reads canvas.width/height each frame — no extra call needed
        })
    }

    update() {
        const t        = this.time.elapsed * 0.001
        const analyser = this.experience.world.audioAnalyser
        const isActive = analyser?.isPlaying ?? false

        // Simulated fallback preserves pulsing animation when no audio is playing.
        // Real values are used when AudioAnalyser is active (audio loaded and playing).
        const audioLevel = isActive ? (analyser.audioLevel ?? 0) : Math.pow(Math.abs(Math.sin(t * 0.9)), 3.0)
        const bass       = isActive ? analyser.bass             : Math.pow(Math.abs(Math.sin(t * 0.5)), 2.0)
        const mid        = isActive ? analyser.mid              : Math.pow(Math.abs(Math.sin(t * 1.1)), 2.0)
        const treble     = isActive ? analyser.high             : Math.pow(Math.abs(Math.sin(t * 1.8)), 2.0)

        this.cards.forEach(card => {
            const dt = Math.min(this.time.delta * 0.001, 0.1)
            card.hoverCurrent += (card.hoverTarget - card.hoverCurrent) * Math.min(1, dt * 3.0)

            card.preset.tick({ time: t, audioLevel, bass, mid, treble, hover: card.hoverCurrent })
        })
    }

    destroy() {
        this.cards.forEach(({ preset }) => preset.destroy())
        this.cards = []
    }
}
